/**
 * Datos de muestra para ver el panel sin levantar la plataforma.
 *
 * Solo en desarrollo y solo con `?maqueta` en la dirección: `import.meta.env.DEV`
 * es una constante que el empaquetador reemplaza por `false` en producción, y
 * con ella el resto de este archivo deja de alcanzarse y se descarta. Nada de
 * esto llega al paquete que se despliega.
 *
 * Existe para poder revisar el diseño —tema claro, teléfono, estados raros—
 * sin un ClickHouse, un portal y una sesión de por medio. Los datos son
 * inventados pero coherentes: lo que suma la serie es lo que dice el resumen.
 */

export function usaMaqueta(): boolean {
  return import.meta.env.DEV && new URLSearchParams(window.location.search).has('maqueta');
}

/** Un generador pseudoaleatorio fijo: la misma pantalla cada vez que se abre. */
function semilla(n: number): () => number {
  let x = n >>> 0;
  return () => {
    x = (x * 1664525 + 1013904223) >>> 0;
    return x / 0xffffffff;
  };
}

interface Perfil {
  nombre: string;
  peso: number;
  falla: number;
  negocio: number;
  abandono: number;
  p95: number;
}

const PERFILES: Perfil[] = [
  { nombre: 'iniciar_sesion', peso: 0.38, falla: 0.012, negocio: 0.02, abandono: 0.01, p95: 640 },
  { nombre: 'pago_tarjeta', peso: 0.22, falla: 0.07, negocio: 0.03, abandono: 0.08, p95: 1900 },
  { nombre: 'avance_efectivo', peso: 0.14, falla: 0.015, negocio: 0.72, abandono: 0.05, p95: 1250 },
  { nombre: 'reordenar_deuda', peso: 0.1, falla: 0.03, negocio: 0.22, abandono: 0.15, p95: 980 },
  { nombre: 'transferir', peso: 0.16, falla: 0.008, negocio: 0.09, abandono: 0.04, p95: 720 },
];

function pasoDe(desdeMs: number, hastaMs: number): number {
  const horas = (hastaMs - desdeMs) / 3_600_000;
  if (horas > 24 * 31) return 86400;
  if (horas <= 3) return 300;
  if (horas <= 48) return 3600;
  if (horas <= 240) return 3 * 3600;
  return 6 * 3600;
}

function iso(ms: number, paso: number): string {
  const d = new Date(ms).toISOString();
  return paso >= 86400 ? d.slice(0, 10) : `${d.slice(0, 10)} ${d.slice(11, 19)}`;
}

/** La serie de uno o de todos los procesos. */
function serie(desdeMs: number, hastaMs: number, proceso: string | null) {
  const paso = pasoDe(desdeMs, hastaMs);
  const pasoMs = paso * 1000;
  const perfiles = proceso ? PERFILES.filter((p) => p.nombre === proceso) : PERFILES;
  const puntos: Array<Record<string, string | number>> = [];

  for (let t = Math.floor(desdeMs / pasoMs) * pasoMs; t <= hastaMs; t += pasoMs) {
    const hora = new Date(t).getUTCHours() - 3; // hora de Chile, aproximada
    const diaria = 0.55 + 0.45 * Math.sin(((hora - 6) / 24) * 2 * Math.PI);
    const rnd = semilla(Math.floor(t / 60_000));
    // Un pico de fallas y de rechazos en el último tramo de la ventana.
    const reciente = hastaMs - t < Math.max(2 * 3_600_000, pasoMs * 2);
    let ejecuciones = 0;
    let ok = 0;
    let fallas = 0;
    let negocio = 0;
    let abandonos = 0;
    let p95 = 0;
    for (const p of perfiles) {
      const n = Math.max(0, Math.round((paso / 3600) * 150 * p.peso * diaria * (0.8 + rnd() * 0.4)));
      const f = Math.round(n * p.falla * (reciente && p.nombre === 'pago_tarjeta' ? 3.2 : 1) * (0.6 + rnd() * 0.8));
      const ne = Math.round(n * p.negocio * (0.7 + rnd() * 0.6));
      const a = Math.round(n * p.abandono * (0.6 + rnd() * 0.8));
      ejecuciones += n;
      fallas += Math.min(f, n);
      negocio += Math.min(ne, n);
      abandonos += Math.min(a, n);
      p95 = Math.max(p95, n > 0 ? p.p95 * (0.8 + rnd() * 0.5) : 0);
    }
    negocio = Math.min(negocio, ejecuciones - fallas);
    abandonos = Math.min(abandonos, ejecuciones - fallas - negocio);
    ok = Math.max(0, ejecuciones - fallas - negocio - abandonos);
    if (ejecuciones === 0) continue;
    puntos.push({
      t: iso(t, paso),
      ejecuciones: String(ejecuciones),
      ok: String(ok),
      con_error: String(fallas),
      con_negocio: String(negocio),
      abandonados: String(abandonos),
      p95_ms: Math.round(p95),
    });
  }
  return { fuente: paso >= 86400 ? 'diario' : 'crudo', paso_segundos: paso, puntos };
}

function resumen(desdeMs: number, hastaMs: number) {
  return PERFILES.map((p) => {
    const { puntos } = serie(desdeMs, hastaMs, p.nombre);
    const suma = (k: string) => puntos.reduce((s, x) => s + Number(x[k]), 0);
    return {
      proceso: p.nombre,
      ejecuciones: String(suma('ejecuciones')),
      ok: String(suma('ok')),
      con_error: String(suma('con_error')),
      con_negocio: String(suma('con_negocio')),
      abandonados: String(suma('abandonados')),
      p95_ms: p.p95,
    };
  }).sort((a, b) => Number(b.ejecuciones) - Number(a.ejecuciones));
}

const SESION = {
  usuario: { email: 'mtapia@voxcom.cl', nombre: 'Mario Tapia', segundoFactor: true, codigosRespaldoRestantes: 8 },
  tenant: { id: 'tricot', nombre: 'Tricot', rol: 'admin', facturacion: { estado: 'al_dia', graciaHasta: null } },
  permisos: { puedeVerServicios: true, puedeAdministrar: true },
  tenants: [{ id: 'tricot', nombre: 'Tricot', rol: 'admin' }],
};

const RUTAS_LENTAS = [
  { ruta: 'POST /tricard-core-service/v1/withdrawals/beginning', servicio: 'tricard-front', llamadas: '412', errores: '9', p50_ms: 900, p95_ms: 1900, p99_ms: 3100 },
  { ruta: 'POST /pivot/v1/pay', servicio: 'tricard-front', llamadas: '288', errores: '21', p50_ms: 760, p95_ms: 1600, p99_ms: 2800 },
  { ruta: 'GET /tricard-core-service/v1/customers/{id}', servicio: 'tricard-front', llamadas: '1840', errores: '3', p50_ms: 210, p95_ms: 640, p99_ms: 980 },
  { ruta: 'GET /wp-json/tricard_api/v1/seguros', servicio: 'tricard-front', llamadas: '1320', errores: '0', p50_ms: 150, p95_ms: 410, p99_ms: 600 },
];

const HTTP = [
  { servicio: 'tricard-front', ruta: 'POST /pivot/v1/pay', metodo: 'POST', llamadas: 288, ok: 250, servidor: 21, contrato: 0, negocio: 17, tasa_servidor: 7.3, tasa_contrato: 0 },
  { servicio: 'tricard-front', ruta: 'POST /tricard-core-service/v1/withdrawals/beginning', metodo: 'POST', llamadas: 412, ok: 140, servidor: 4, contrato: 0, negocio: 268, tasa_servidor: 1, tasa_contrato: 0 },
];

/** Un día y una hora de Chile para el encabezado del informe, derivados de «ahora». */
const ahoraISO = () => new Date().toISOString();

/**
 * El informe de análisis. Cuenta una sola historia coherente con el resto de la maqueta:
 * los pagos con tarjeta fallan en web porque un servicio responde 503, la app móvil
 * está sana, y un proceso viene perdiendo gente en el segundo paso desde antes.
 */
const INFORME = {
  generado: ahoraISO(),
  ventana: { desde: new Date(Date.now() - 24 * 3_600_000).toISOString(), hasta: ahoraISO() },
  fuente: 'crudo',
  salud: [
    { canal: 'web', ejecuciones: 4820, tasa_error: 3.1, tasa_negocio: 9.4, tasa_abandono: 4.6, p50_ms: 420, p95_ms: 1300, p99_ms: 2800 },
    { canal: 'app_mobile', ejecuciones: 2310, tasa_error: 1.4, tasa_negocio: 8.8, tasa_abandono: 6.2, p50_ms: 520, p95_ms: 1800, p99_ms: 3400 },
  ],
  hallazgos: [
    {
      id: 'h1',
      titulo: 'Fallan los pagos con tarjeta en web',
      severidad: 'alta',
      origen: 'anomalia',
      sintoma: 'La tasa de error de pago_tarjeta subió de 5,0 % a 18,0 % en la última hora, comparada con los últimos 7 días a la misma hora.',
      alcance: {
        clase: 'un_canal',
        afectados: ['web'],
        sanos: ['app_mobile'],
        conclusion: 'Solo en web; app_mobile está normal con tráfico comparable. El backend es común a los dos, así que apunta al cliente de web.',
      },
      causaRaiz: {
        clase: 'servicio_dominante',
        conclusion: '74% del tiempo propio está en POST /pivot/v1/pay (web-front): 3.800 ms. El resto del árbol es ruido al lado de eso.',
        atribucion: [
          { servicio: 'web-front', operacion: 'POST /pivot/v1/pay', propio_ms: 3800, pct: 74 },
          { servicio: 'web-front', operacion: 'GET /core-service/v1/customers/{id}', propio_ms: 210, pct: 4 },
          { servicio: 'web-front', operacion: 'pago_tarjeta', propio_ms: 110, pct: 2 },
        ],
        excepciones: [{ tipo: 'HTTP 503', veces: 21 }],
      },
      acciones: [
        { texto: 'Revisar el servicio de pagos (POST /pivot/v1/pay) y su último despliegue.', porque: 'Concentra el 74% del tiempo de las ejecuciones con error y responde 503.' },
        { texto: 'Confirmar si web envía algo distinto que la app móvil en esa llamada.', porque: 'El mismo servicio atiende a los dos canales y solo uno falla.' },
      ],
      evidencia: { proceso: 'pago_tarjeta', paso: null, ruta: 'POST /pivot/v1/pay', trazas: ['a1b2c3d4e5f60718293a4b5c6d7e8f90', '0f9e8d7c6b5a49382716a5b4c3d2e1f0'] },
    },
    {
      id: 'h2',
      titulo: 'Se ralentiza reordenar_deuda en la app móvil',
      severidad: 'media',
      origen: 'anomalia',
      sintoma: 'El p95 de reordenar_deuda pasó de 1,70 s a 2,40 s con la versión 2.1.31 de la app, contra la 2.1.24.',
      alcance: {
        clase: 'un_canal',
        afectados: ['app_mobile'],
        sanos: ['web'],
        conclusion: 'Solo en app_mobile; web está normal con tráfico comparable. El backend es común a los dos, así que apunta al cliente de app_mobile.',
      },
      causaRaiz: null,
      acciones: [
        { texto: 'Comparar la 2.1.31 con la 2.1.24 en la pestaña Releases, paso por paso.', porque: 'Muestra en qué paso del proceso se perdió el tiempo.' },
      ],
      evidencia: { proceso: 'reordenar_deuda', paso: null, ruta: null, trazas: ['5d4c3b2a190807f6e5d4c3b2a1908070'] },
    },
    {
      id: 'h3',
      titulo: 'Se cae la gente en el segundo paso de avance_efectivo',
      severidad: 'baja',
      origen: 'cronico',
      sintoma: 'El 65% de quienes empiezan avance_efectivo no llega al segundo paso. Viene así desde antes de esta ventana, no es un cambio reciente.',
      alcance: {
        clase: 'transversal',
        afectados: ['web', 'app_mobile'],
        sanos: [],
        conclusion: 'Aparece en todos los canales con tráfico (web, app_mobile). Lo que comparten es el backend, así que ahí conviene mirar antes que en los clientes.',
      },
      causaRaiz: null,
      acciones: [
        { texto: 'Mirar el embudo de avance_efectivo y qué responde el servicio en el primer paso.', porque: 'La mayoría de las caídas son rechazos de negocio, no fallas técnicas.' },
      ],
      evidencia: { proceso: 'avance_efectivo', paso: 'avance.paso_2', ruta: null, trazas: ['9c8b7a6f5e4d3c2b1a09f8e7d6c5b4a3'] },
    },
  ],
  limitaciones: [
    'No se ven crashes, bloqueos ni cuadros perdidos. Eso exige el módulo nativo, que hay que activar con `senalesNativas: true` al inicializar el SDK. Lo que sí se ve de la app son sus procesos de negocio y su latencia.',
    'Los datos de esta pantalla son de ejemplo.',
  ],
};

const RELEASES = [
  { app_version: '2.1.31', ejecuciones: '5120', desde: '2026-10-05 08:00:00', hasta: '2026-10-05 16:00:00', canales: ['web', 'app_mobile'], plataformas: ['web', 'android'] },
  { app_version: '2.1.30', ejecuciones: '4380', desde: '2026-09-28 08:00:00', hasta: '2026-10-05 07:59:00', canales: ['web'], plataformas: ['web'] },
  { app_version: '2.1.24', ejecuciones: '1210', desde: '2026-09-14 08:00:00', hasta: '2026-10-05 16:00:00', canales: ['app_mobile'], plataformas: ['android'] },
];

type VeredictoMaqueta = 'peor' | 'mejor' | 'igual' | 'sin_datos';

function celda(a: number, b: number, veredicto: VeredictoMaqueta, contrastada: boolean, motivo: string) {
  return { a, b, delta: Number((b - a).toFixed(1)), deltaRelativo: a === 0 ? null : (b - a) / a, veredicto, contrastada, motivo };
}

/** Una comparación entre dos releases con un empeoramiento claro, una mejora y un empate. */
function compararMaqueta(a: string, b: string) {
  const fila = (
    proceso: string,
    paso: string,
    indice: number,
    ea: number,
    eb: number,
    err: ReturnType<typeof celda>,
    aba: ReturnType<typeof celda> | null,
    lat: ReturnType<typeof celda>,
    veredicto: VeredictoMaqueta
  ) => ({
    proceso,
    paso,
    indice,
    a: { ejecuciones: ea, p50_ms: lat.a * 0.5, p95_ms: lat.a },
    b: { ejecuciones: eb, p50_ms: lat.b * 0.5, p95_ms: lat.b },
    error: err,
    abandono: aba,
    latencia: lat,
    veredicto,
  });
  const contraste = 'La diferencia supera la variabilidad esperable para esta cantidad de ejecuciones.';
  const ruido = 'La diferencia cabe dentro de la variabilidad esperable: se trata como sin cambio.';
  const indicativo = 'Solo se le exige que el cambio sea grande: es indicativo, no probado.';
  return {
    a,
    b,
    fuente: 'crudo',
    veredicto: 'peor',
    filas: [
      fila('pago_tarjeta', '', 0, 1020, 1190, celda(4.9, 12.6, 'peor', true, contraste), celda(7.8, 8.4, 'igual', true, ruido), celda(1900, 3350, 'peor', false, indicativo), 'peor'),
      fila('pago_tarjeta', 'pago.datos', 0, 960, 1090, celda(0.6, 0.8, 'igual', true, ruido), null, celda(420, 450, 'igual', false, indicativo), 'igual'),
      fila('pago_tarjeta', 'pago.confirmar', 1, 880, 940, celda(3.1, 10.8, 'peor', true, contraste), null, celda(1400, 3050, 'peor', false, indicativo), 'peor'),
      fila('iniciar_sesion', '', 0, 1640, 1710, celda(1.4, 1.1, 'igual', true, ruido), celda(1.2, 0.9, 'igual', true, ruido), celda(700, 610, 'mejor', false, indicativo), 'mejor'),
      fila('transferir', '', 0, 690, 720, celda(0.9, 0.8, 'igual', true, ruido), celda(4.2, 3.6, 'igual', true, ruido), celda(740, 730, 'igual', false, indicativo), 'igual'),
      fila('reordenar_deuda', '', 0, 38, 41, celda(3.0, 4.9, 'sin_datos', true, 'Menos de 100 ejecuciones por lado: no alcanza para decidir.'), null, celda(1000, 1050, 'sin_datos', false, indicativo), 'sin_datos'),
    ],
  };
}

/** Devuelve la respuesta de muestra, o `undefined` si esa ruta no está cubierta. */
export function responderMaqueta(url: URL): unknown {
  const ruta = url.pathname;
  const hastaMs = Date.parse(url.searchParams.get('to') ?? '') || Date.now();
  const desdeMs = Date.parse(url.searchParams.get('from') ?? '') || hastaMs - 24 * 3_600_000;
  const proceso = url.searchParams.get('proceso');

  if (ruta.startsWith('/api/trazas/')) {
    const inicio = Date.now() - 5 * 60_000;
    const marca = (ms: number) => iso(inicio + ms, 1) + '.000';
    const span = (id: string, padre: string, nombre: string, ms: number, dur: number, error: boolean, atributos: Record<string, string>) => ({
      Timestamp: marca(ms), SpanId: id, ParentSpanId: padre, SpanName: nombre, SpanKind: 'Client', ServiceName: 'tricard-front',
      Duration: String(dur * 1e6), StatusCode: error ? 'Error' : 'Unset', StatusMessage: error ? 'HTTP 503' : '', SpanAttributes: atributos,
    });
    return {
      spans: [
        span('raiz', '', 'pago_tarjeta', 0, 4120, true, { 'vox.kind': 'process', 'vox.process.name': 'pago_tarjeta', 'vox.result': 'ERROR' }),
        span('a', 'raiz', 'GET /tricard-core-service/v1/customers/{id}', 40, 210, false, { 'http.request.method': 'GET', 'url.full': 'https://api-dv.tricard.cl:9443/tricard-core-service/v1/customers/{id}' }),
        span('b', 'raiz', 'POST /pivot/v1/pay', 300, 3800, true, { 'http.request.method': 'POST', 'http.response.status_code': '503', 'url.full': 'https://api-dv.tricard.cl:9443/pivot/v1/pay' }),
      ],
    };
  }

  switch (ruta) {
    case '/auth/yo':
      return SESION;
    case '/api/serie':
      return serie(desdeMs, hastaMs, proceso);
    case '/api/resumen':
      return { fuente: 'crudo', procesos: resumen(desdeMs, hastaMs) };
    case '/api/procesos':
      return {
        fuente: 'crudo',
        procesos: resumen(desdeMs, hastaMs).map((p) => ({ process_name: p.proceso, ...p, abandonados: p.abandonados, p50_ms: Math.round(p.p95_ms / 2) })),
      };
    case '/api/canales':
      return {
        fuente: 'crudo',
        disponibles: [
          { canal: 'web', conTrafico: true },
          { canal: 'app_mobile', conTrafico: true },
        ],
        comparativa: [
          { canal: 'web', ejecuciones: '980', ok: '936', con_error: '14', abandonados: '30', p50_ms: 420, p95_ms: 1300 },
          { canal: 'app_mobile', ejecuciones: '640', ok: '586', con_error: '24', abandonados: '30', p50_ms: 520, p95_ms: 1800 },
        ],
      };
    case '/api/anomalias':
      return {
        anomalias: [
          { tipo: 'linea_base', metrica: 'tasa_error', ambito: 'pago_tarjeta', canal: 'web', severidad: 'alta', actual: 18, esperado: 5, cambioRelativo: 2.6, muestras: 120, explicacion: 'la tasa de error subió de 5,0% a 18,0%', detalle: 'comparado con 7 días a la misma hora' },
          { tipo: 'version', metrica: 'latencia_p95', ambito: 'reordenar_deuda', canal: 'app_mobile', severidad: 'media', actual: 2400, esperado: 1700, cambioRelativo: 0.41, muestras: 64, explicacion: 'el p95 pasó de 1,70 s a 2,40 s', detalle: 'versión 2.1.31 contra 2.1.24' },
        ],
      };
    case '/api/ultimo':
      return { ultimo: new Date(Date.now() - 4000).toISOString().replace('T', ' ').slice(0, 19) };
    case '/api/recientes':
      return {
        sin_ejemplos: null,
        ejecuciones: [
          { trace_id: 'a'.repeat(32), process_id: 'p1', process_name: 'pago_tarjeta', ts: iso(Date.now() - 3 * 60_000, 1), result: 'ERROR', duration_ms: 4120, platform: 'web', canal: 'web', app_version: '2.1.31', desenlace: 'falla_tecnica', causa: 'servicio', llamada: 'POST /pivot/v1/pay', codigo: 503 },
          { trace_id: 'b'.repeat(32), process_id: 'p2', process_name: 'avance_efectivo', ts: iso(Date.now() - 7 * 60_000, 1), result: 'BUSINESS_ERROR', duration_ms: 2020, platform: 'web', canal: 'web', app_version: '2.1.31', desenlace: 'rechazado_en_evaluacion', causa: 'negocio', llamada: 'POST /tricard-core-service/v1/withdrawals/beginning', codigo: 409 },
          { trace_id: 'c'.repeat(32), process_id: 'p3', process_name: 'reordenar_deuda', ts: iso(Date.now() - 12 * 60_000, 1), result: 'ABANDONED', duration_ms: 189055, platform: 'android', canal: 'app_mobile', app_version: '2.1.24', desenlace: '', causa: '', llamada: '', codigo: 0 },
          { trace_id: 'd'.repeat(32), process_id: 'p4', process_name: 'iniciar_sesion', ts: iso(Date.now() - 19 * 60_000, 1), result: 'ERROR', duration_ms: 880, platform: 'android', canal: 'app_mobile', app_version: '2.1.24', desenlace: '', causa: 'otro', llamada: '', codigo: 0 },
        ],
      };
    case '/api/servicios':
      return { servicios: RUTAS_LENTAS, http: HTTP, fuente: 'metricas' };
    case '/auth/despliegue':
      return {
        servicios: [
          { servicio: 'ingesta', ok: true, commit: '181dd91', latenciaMs: 38 },
          { servicio: 'consulta', ok: true, commit: '181dd91', latenciaMs: 52 },
          { servicio: 'alertas', ok: true, commit: '181dd91', latenciaMs: 41 },
          { servicio: 'panel', ok: true, commit: '181dd91', latenciaMs: null },
        ],
      };
    case '/api/versiones':
      return {
        versiones: [
          { canal: 'web', platform: 'web', sdk: '0.4.1', app: '2.1.31', spans: '48210', visto: '2026-10-05 15:59:00' },
          { canal: 'app_mobile', platform: 'android', sdk: '0.4.1', app: '2.1.24', spans: '9120', visto: '2026-10-05 15:58:00' },
        ],
      };
    case '/api/embudo':
      return {
        fuente: 'crudo', proceso: proceso ?? 'avance_efectivo', ejecuciones: 412, completados: 118, con_error: 6, abandonados: 20, con_negocio: 268,
        pasos: [
          { paso: 'avance.paso_1', indice: 1, llegaron: 412, errores: 0, alcance_pct: 100, caida_pct: 0, p50_ms: 900, p95_ms: 1900 },
          { paso: 'avance.paso_2', indice: 2, llegaron: 144, errores: 3, alcance_pct: 35, caida_pct: 65, p50_ms: 1200, p95_ms: 2400 },
          { paso: 'avance.paso_3', indice: 3, llegaron: 118, errores: 3, alcance_pct: 28.6, caida_pct: 18, p50_ms: 800, p95_ms: 1700 },
        ],
      };
    case '/api/ejemplos':
      return { sin_ejemplos: null, ejemplos: [] };
    case '/api/desenlaces':
      return { desenlaces: [], causas: { servicio: 4, negocio: 268, otro: 2 }, sin_desglose: null };
    case '/api/vitals':
      return { vitals: [] };
    case '/api/analisis':
      return INFORME;
    case '/informe/enlaces':
      return { enlaces: [] };
    case '/api/releases':
      return { fuente: 'crudo', releases: RELEASES };
    case '/api/comparar':
      return compararMaqueta(url.searchParams.get('a') ?? '2.1.30', url.searchParams.get('b') ?? '2.1.31');
    default:
      return undefined;
  }
}
